import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, FileText, Clock, ShieldCheck, Globe2 } from "lucide-react";
import { http, money, trackEvent, errMsg } from "../lib/market";
import { Eyebrow, GlassCard } from "../components/Section";

const Block = ({ title, items, icon: Icon }) => (
  <GlassCard>
    <Eyebrow className="mb-4">{title}</Eyebrow>
    <ul className="space-y-2.5">
      {items.map((x) => (
        <li key={x} className="flex gap-2.5 font-inter text-sm text-white/75"><Icon className="w-4 h-4 text-turquoise shrink-0 mt-0.5" />{x}</li>
      ))}
    </ul>
  </GlassCard>
);

export default function ServiceDetail() {
  const { serviceId } = useParams();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    http.get(`/services/${serviceId}`).then(({ data }) => {
      setS(data);
      trackEvent("service_detail_viewed", { service_id: data.service_id, category_id: data.category_id });
    }).catch((e) => setErr(errMsg(e)));
  }, [serviceId]);

  if (err) return <main className="bg-ink text-white min-h-screen p-16 font-inter" data-testid="service-detail-error">{err}</main>;
  if (!s) return <main className="bg-ink min-h-screen" />;

  return (
    <main className="bg-ink text-white min-h-screen py-12" data-testid="service-detail-page">
      <div className="container mx-auto px-5 sm:px-8 max-w-6xl">
        <Link to="/services" className="btn-aj-ghost text-xs mb-8 inline-flex" data-testid="service-back-link"><ArrowLeft className="w-3.5 h-3.5" /> All services</Link>
        <div className="grid lg:grid-cols-[1fr_340px] gap-10">
          <div>
            <Eyebrow>{s.category_name}</Eyebrow>
            <h1 className="font-playfair text-4xl sm:text-5xl mt-3 leading-tight" data-testid="service-detail-name">{s.service_name}</h1>
            <p className="font-inter text-base text-white/65 mt-5 leading-relaxed max-w-2xl">{s.description}</p>
            <div className="grid md:grid-cols-2 gap-5 mt-10">
              <Block title="Deliverables" items={s.deliverables} icon={CheckCircle2} />
              <Block title="Documents that help" items={s.required_documents} icon={FileText} />
              <Block title="Lawyer qualifications" items={s.required_qualifications} icon={ShieldCheck} />
              <Block title="Completed when" items={[s.completion_criteria]} icon={CheckCircle2} />
            </div>
          </div>
          <aside>
            <GlassCard className="sticky top-28 !p-7 border-turquoise/20">
              <div className="font-montserrat text-[10px] uppercase tracking-[0.2em] text-white/45">Professional fee</div>
              <div className="font-playfair text-4xl text-gold-200 mt-2" data-testid="service-detail-fee">{s.fixed_fee_minor ? money(s.fixed_fee_minor) : "Quoted by lawyer"}</div>
              <p className="font-inter text-xs text-white/45 mt-2 leading-relaxed">
                The lawyer receives 100% of the agreed fee (20% upfront, 80% on completion). Any platform fee is shown separately at checkout.
              </p>
              <div className="border-t border-white/8 my-6" />
              <div className="space-y-3 font-inter text-sm text-white/70">
                <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-turquoise" /> {s.estimated_turnaround}</div>
                <div className="flex items-center gap-2" data-testid="service-detail-availability"><Globe2 className="w-4 h-4 text-turquoise" />
                  {s.available ? `Available in ${s.available_in.join(", ")}` : "Not yet available in your jurisdiction"}</div>
                {s.wallet_eligibility && <div className="text-gold-200 text-xs">Eligible for Justice Wallet sponsorship</div>}
              </div>
              {s.available && (
                <Link to={`/request-service?service=${s.service_id}`} data-testid="service-request-btn" className="btn-aj-primary w-full mt-7">
                  Request this service <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </GlassCard>
          </aside>
        </div>
      </div>
    </main>
  );
}
